import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mapValues } from 'lodash';
import { useContext, useEffect } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  ApiError,
  ProviderCredentialingRatesAndDifferentials,
  ProviderService,
  TravelTypeEnum,
  WorkTypeEnum,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import { FormFieldVisibility, FormLabel } from '../../../app/components/FormLabel';
import { FormTextField } from '../../../app/components/FormTextField';
import { UnsavedChangesFormPrompt } from '../../../app/components/UnsavedChangesFormPrompt';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { useBlockInvalidChar } from '../../../app/hooks/block-invalid-char';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { userQueryKeys } from '../../../app/query-keys-factories/user';
import { handleApiError } from '../../../app/utils/errorhandling';
import { ProviderProfileCreateOutletContext } from '../../Registration/ProviderProfileCreate';
import { SHIFT_LABEL_INFO_TEXT, SHIFT_OPTIONS } from './models';
import { ProviderFormContainer } from './ProviderFormContainer';
import { ProviderRateControl } from './ProviderRateControl';

const validationSchema = z
  .object({
    shift_limit: z.coerce
      .string()
      .min(1, { message: 'Shift limit must be provided.' })
      .pipe(
        z.coerce
          .number()
          .min(1, { message: 'Your max shifts per month must be greater than or equal to 1.' })
          .max(30, { message: 'Your max shifts per month must be less than or equal to 30.' })
      ),
    shift_rate_day_availability: z.coerce.boolean(),
    shift_rate_night_availability: z.coerce.boolean(),
    shift_rate_call_availability: z.coerce.boolean(),
    shift_rate_24h_availability: z.coerce.boolean(),
    shift_rate_day: z.coerce.number(),
    shift_rate_night: z.coerce.number(),
    shift_rate_call: z.coerce.number(),
    shift_rate_24h: z.coerce.number(),
    work_type: z.nativeEnum(WorkTypeEnum, {
      required_error: 'Please select your work type preference',
    }),
    travel_type: z.nativeEnum(TravelTypeEnum, {
      required_error: 'Please select your travel type preference',
    }),
  })
  .refine((data) => !data.shift_rate_day_availability || data.shift_rate_day, {
    message: 'Day shift rate must be greater than or equal to 1.',
    path: ['shift_rate_day'],
  })
  .refine((data) => !data.shift_rate_night_availability || data.shift_rate_night, {
    message: 'Night shift rate must be greater than or equal to 1.',
    path: ['shift_rate_night'],
  })
  .refine((data) => !data.shift_rate_call_availability || data.shift_rate_call, {
    message: 'Call shift rate must be greater than or equal to 1.',
    path: ['shift_rate_call'],
  })
  .refine((data) => !data.shift_rate_24h_availability || data.shift_rate_24h, {
    message: '24h shift rate must be greater than or equal to 1.',
    path: ['shift_rate_24h'],
  });

export const ProviderRatesForm = () => {
  useDocumentTitle('Provider Rates');
  const { formRef, onSuccess } = useOutletContext<ProviderProfileCreateOutletContext>() ?? {};
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const {
    isLoading: isProviderLoading,
    error: providerFetchError,
    data: provider,
  } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingRatesAndDifferentials(),
    queryFn: async () =>
      await ProviderService.providerCurrentCredentialingRatesDifferentialsRetrieve(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { isDirty, isSubmitSuccessful, errors },
    watch,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const blockInvalidChar = useBlockInvalidChar(/[\d]/);

  const resetControl = (name): void => {
    setValue(name, null);
  };

  useEffect(() => {
    reset({
      ...provider,
      shift_limit:
        (provider?.shift_limit || 0) <= 30 && provider?.shift_limit !== null
          ? provider?.shift_limit?.toString()
          : '',
      shift_rate_day_availability: !!provider?.shift_rate_day,
      shift_rate_night_availability: !!provider?.shift_rate_night,
      shift_rate_call_availability: !!provider?.shift_rate_call,
      shift_rate_24h_availability: !!provider?.shift_rate_24h,
    });
  }, [provider]);

  const queryClient = useQueryClient();
  const mutationCurrentProviderPartialUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingRatesAndDifferentials) => {
      return ProviderService.providerCurrentCredentialingRatesDifferentialsUpdate({
        requestBody: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: providerQueryKeys.all(),
      });

      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail('current'),
      });

      toast.success('Your Profile has been updated');
      onSuccess?.();
    },
    onError: (error: ApiError) => {
      handleApiError(error);
    },
  });

  const onSubmit = (data) => {
    delete data.shift_rate_day_availability;
    delete data.shift_rate_night_availability;
    delete data.shift_rate_call_availability;
    delete data.shift_rate_24h_availability;

    const submissionData = mapValues(data, (value) => (value === 0 ? null : value));
    mutationCurrentProviderPartialUpdate.mutateAsync(submissionData);
  };

  return (
    <>
      {isProviderLoading && (
        <Row>
          <Col xs={12} className="d-flex align-items-center justify-content-center">
            <Spinner animation="grow" className="loading-spinner" variant="primary" />
          </Col>
        </Row>
      )}
      {!!providerFetchError && (
        <Row className="row-cols-auto justify-content-center">
          <Col>
            <Alert variant="danger" show={!!providerFetchError}>
              <Alert.Heading>Something went wrong</Alert.Heading>
              <p>Please try again later. If the problem persists, please contact us.</p>
            </Alert>
          </Col>
        </Row>
      )}

      {provider && (
        <Card className="p-4">
          <h2 className="border-bottom pb-2 mb-4">Rates</h2>
          <Form noValidate onSubmit={handleSubmit(onSubmit)} ref={formRef}>
            <div className="mb-4 d-flex flex-column align-items-start">
              <FormLabel visibility={FormFieldVisibility.PUBLIC}>Shift Types and Rate</FormLabel>
              <small className="text-muted" style={{ marginTop: 2 }}>
                {SHIFT_LABEL_INFO_TEXT}
              </small>
            </div>
            {SHIFT_OPTIONS.map((shift, idx) => {
              return (
                <ProviderRateControl
                  shift={shift}
                  key={idx}
                  register={register}
                  control={control}
                  errors={errors}
                  blockInvalidChar={blockInvalidChar}
                  onResetControl={resetControl}
                />
              );
            })}

            <Row>
              <Col xs={12} md={6}>
                <FormLabel visibility={FormFieldVisibility.PUBLIC}>
                  How many shifts are you open to working?
                </FormLabel>
                <div className="mb-4 mt-2">
                  {Object.values(WorkTypeEnum).map((workType) => (
                    <div
                      key={workType}
                      className={`mb-3 border rounded p-2 ${
                        watch('work_type') === workType ? 'bg-primary-muted' : ''
                      }`}
                    >
                      <Form.Check
                        type="radio"
                        id={`work-type-${workType}`}
                        {...register('work_type', {
                          required: 'Please select your work type preference',
                        })}
                        value={workType}
                        label={
                          <div className="d-flex flex-column">
                            <span className="fw-bold">{workType}</span>
                            <span className="text-muted small">
                              {workType === WorkTypeEnum.SIDE_GIG && '2 to 4 shifts per month'}
                              {workType === WorkTypeEnum.PART_TIME && '5 to 8 shifts per month'}
                              {workType === WorkTypeEnum.FULL_TIME && '9+ shifts per month'}
                            </span>
                          </div>
                        }
                        isInvalid={!!errors.work_type}
                      />
                    </div>
                  ))}
                  {errors.work_type && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.work_type.message}
                    </Form.Control.Feedback>
                  )}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <FormLabel visibility={FormFieldVisibility.PUBLIC}>
                  Where are you looking to work?
                </FormLabel>
                <div className="mb-4 mt-2">
                  {Object.values(TravelTypeEnum).map((travelType) => (
                    <div
                      key={travelType}
                      className={`mb-3 border rounded p-2 ${
                        watch('travel_type') === travelType ? 'bg-primary-muted' : ''
                      }`}
                    >
                      <Form.Check
                        type="radio"
                        id={`work-type-${travelType}`}
                        {...register('travel_type', {
                          required: 'Please select your work type preference',
                        })}
                        value={travelType}
                        label={
                          <div className="d-flex flex-column">
                            <span className="fw-bold">{travelType}</span>
                            <span className="text-muted small">
                              {travelType === TravelTypeEnum.LOCAL && 'Keep it close to home'}
                              {travelType === TravelTypeEnum.REGIONAL &&
                                'Not opposed to some travel'}
                              {travelType === TravelTypeEnum.NATIONAL &&
                                'Willing to travel anywhere'}
                            </span>
                          </div>
                        }
                        isInvalid={!!errors.work_type}
                      />
                    </div>
                  ))}
                  {errors.work_type && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.travelType.message}
                    </Form.Control.Feedback>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <FormTextField
                  name="shift_limit"
                  labelClassNames=""
                  label="Max Shifts per Month"
                  type="number"
                  className="mb-5 position-relative"
                  min={0}
                  step="any"
                  register={register}
                  errors={errors}
                  required
                  visibility={FormFieldVisibility.PUBLIC}
                  onKeyDown={blockInvalidChar}
                  inputInfo="Shifts / Month"
                />
              </Col>
              <Col />
            </Row>
            <Row>
              <Col md={12}>
                <Button
                  disabled={mutationCurrentProviderPartialUpdate.isLoading || !isSubmitEnabled}
                  className="float-end"
                  variant="primary"
                  type="submit"
                >
                  Save Changes
                </Button>
              </Col>
            </Row>
          </Form>
          <UnsavedChangesFormPrompt hasUnsavedChanges={isDirty && !isSubmitSuccessful} />
        </Card>
      )}
    </>
  );
};

export const ProviderRates = () => {
  return (
    <ProviderFormContainer>
      <ProviderRatesForm />
    </ProviderFormContainer>
  );
};
